import {forwardRef, ReactElement, Ref, useCallback, useEffect, useRef, useState} from 'react';
import moment from 'moment';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../i18n/translations';
import {
  Box,
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
  Skeleton,
  Slide,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import {useSnackBar} from '../../../../contexts/snack-bar/context';
import {ContentPage} from '../../../../components/content-page/content-page';
import TaskDetailsCard from '../../../../components/daily-care-log/task-details-card/task-details-card';
import HandleTaskForm from '../../../../components/daily-care-log/handle-task-form/handle-task-form';
import {CareTaskComplementary} from '../../../../backend/interfaces/care-task-complementary';
import {PutEntityCareTask} from '../../../../backend/requests/put-entity-care-task';
import {EntityUserCareTasksRepository} from '../../../../repositories/entity-user-care-tasks';
import {useDailyCareLog} from '../../../../contexts/daily-care-log/context';
import {useEntity} from '../../../../contexts/entity/context';
import {EntityInfo} from '../../../../backend/interfaces/entity-info';
import {EntityUser} from '../../../../backend/responses/entity-user';
import {useAuth} from '../../../../contexts/auth/context';
import {useSeniorProfilePdfExport} from '../../../../hooks/use-senior-profile-pdf-export';
import {TransitionProps} from '@mui/material/transitions';
import {Pallete} from '../../../../theme/bluebird';

type LocationState = {
  task: CareTaskComplementary;
};

const BottomSheetTransition = forwardRef(function BottomSheetTransition(
  props: TransitionProps & {children: ReactElement<any, any>},
  ref: Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const TaskDetailsDailyCareLogScreen = () => {
  const [task, setTask] = useState<CareTaskComplementary>();

  const {t} = useTranslation();

  const dailyCareLogContext = useDailyCareLog();

  let {entityId, careTaskRef}: {entityId: string; careTaskRef: string} = useParams();

  const location = useLocation();

  const history = useHistory();

  const snackBarContext = useSnackBar();
  const {state: entityState} = useEntity();
  const authContext = useAuth();
  const {handleExportPdf} = useSeniorProfilePdfExport({
    errorNotificationChannel: 'daily-care-log',
  });
  const selectedEntity = authContext.state.session?.selectedAffiliation?.entity;
  const currentEntity = selectedEntity?.id === entityId ? selectedEntity : undefined;
  const [actionSenior, setActionSenior] = useState<EntityUser>();
  const [actionsOpen, setActionsOpen] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const isGeneratingPdfRef = useRef(false);
  const pendingPdfExportRef = useRef<{
    seniorId: string;
    entity: EntityInfo;
  } | null>(null);
  const actionSeniorDisplayName = actionSenior?.user.displayName ?? actionSenior?.user.name ?? '';

  const fetchCareTask = useCallback(async () => {
    const remoteTask = await EntityUserCareTasksRepository.instance.GetEntityCareTask(
      entityId,
      careTaskRef
    );
    setTask(remoteTask);
  }, [careTaskRef, entityId]);

  const handleOpenSeniorActions = (senior: EntityUser) => {
    if (!senior.user.id || !currentEntity || isGeneratingPdfRef.current) return;

    setActionSenior(senior);
    setActionsOpen(true);
  };

  const handleSelectPdf = () => {
    const seniorId = actionSenior?.user.id;
    if (!seniorId || !currentEntity || isGeneratingPdfRef.current) return;

    isGeneratingPdfRef.current = true;
    setIsGeneratingPdf(true);
    pendingPdfExportRef.current = {seniorId, entity: currentEntity};
    setActionsOpen(false);
  };

  const handleActionsSheetExited = () => {
    const pendingExport = pendingPdfExportRef.current;
    if (!pendingExport) return;

    pendingPdfExportRef.current = null;
    window.setTimeout(async () => {
      try {
        await handleExportPdf(pendingExport);
      } finally {
        isGeneratingPdfRef.current = false;
        setIsGeneratingPdf(false);
      }
    }, 0);
  };

  const handleTaskSubmit = async (data: PutEntityCareTask) => {
    const userId = task?.careTask.userId;

    if (!userId) return;

    try {
      await EntityUserCareTasksRepository.instance.HandleCareTask(
        entityId,
        userId,
        careTaskRef,
        data
      );

      snackBarContext.setDailyCareLogToastNotification({
        severity: 'success',
        message: t(translations.care_and_activities.handle_task_modal.snackbar.update.success),
      });

      history.goBack();
    } catch (error) {
      snackBarContext.setDailyCareLogToastNotification({
        severity: 'error',
        message: t(translations.care_and_activities.handle_task_modal.snackbar.update.error),
      });

      console.debug('🔴 Error handling the care task', error);
    }
  };

  useEffect(() => {
    if (location.state) {
      setTask((location.state as LocationState).task);
    } else {
      fetchCareTask();
    }
  }, [location.state, fetchCareTask]);

  const titleSx: SxProps<Theme> = {
    color: 'var(--dcm-gray)',
    fontFamily: 'Roboto',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.225px',
  };

  const titleLoadingSkeletonSx: SxProps<Theme> = {
    height: '40px',
    width: '120px',
  };

  useEffect(() => {
    dailyCareLogContext.setNavigationHeader(true, t(translations.daily_care_log.generics.back));
    dailyCareLogContext.toggleBottomNavigation(false);

    return () => {
      dailyCareLogContext.setNavigationHeader(false);
      dailyCareLogContext.toggleBottomNavigation(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ContentPage>
      <Stack direction="column" gap="32px">
        {/**
         * Task `startsAt` and `name`
         */}
        {task ? (
          <Typography sx={titleSx} noWrap>
            {moment(task?.careTask.startsAt).format('H[h]mm')}
            <Typography
              component="span"
              sx={{
                ...titleSx,
                fontWeight: 500,
              }}
            >
              {` ${task?.careTask.nameConfigurationValue || task?.activityTypeName}`}
            </Typography>
          </Typography>
        ) : (
          <Skeleton sx={titleLoadingSkeletonSx} />
        )}

        {/**
         * Card - internally managed skeleton
         */}
        <TaskDetailsCard
          task={task}
          entityId={entityId}
          entitySocialResponses={entityState.availableSocialResponses}
          onSeniorAction={currentEntity ? handleOpenSeniorActions : undefined}
          seniorActionsOpen={actionsOpen}
          seniorActionsDisabled={isGeneratingPdf}
        />

        {/**
         * Form
         */}
        {task ? (
          <HandleTaskForm
            task={task}
            onCancel={() => history.goBack()}
            onSubmit={handleTaskSubmit}
          />
        ) : (
          <>
            <Stack gap={2} flexDirection="row" width="100%">
              <Skeleton
                sx={{
                  height: '50px',
                  width: '50%',
                }}
              />
              <Skeleton
                sx={{
                  height: '50px',
                  width: '50%',
                }}
              />
            </Stack>
            <Stack gap={1} flexDirection="column" width="100%">
              <Skeleton
                sx={{
                  height: '80px',
                  width: '100%',
                }}
              />
              <Skeleton
                sx={{
                  height: '100px',
                  width: '100%',
                }}
              />
            </Stack>
          </>
        )}
      </Stack>
      <Dialog
        open={actionsOpen}
        onClose={() => setActionsOpen(false)}
        onTransitionExited={handleActionsSheetExited}
        TransitionComponent={BottomSheetTransition}
        fullWidth
        maxWidth="sm"
        aria-labelledby="senior-actions-title"
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-end',
            justifyContent: 'center',
          },
          '& .MuiDialog-paper': {
            m: 0,
            width: {xs: '100%', sm: 'min(100%, 480px)'},
            maxWidth: 'none',
            maxHeight: 'min(80vh, 480px)',
            borderRadius: '16px 16px 0 0',
          },
        }}
      >
        <DialogTitle
          id="senior-actions-title"
          component="h2"
          sx={{
            px: 2.5,
            pt: 2.5,
            pb: 1.5,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          }}
        >
          {t(translations.daily_care_log.senior_actions.title, {name: actionSeniorDisplayName})}
        </DialogTitle>
        <DialogContent sx={{px: 1.5, pb: 2.5}}>
          <ButtonBase
            component="button"
            type="button"
            aria-label={t(translations.export.senior_profile.save_pdf)}
            disabled={isGeneratingPdf}
            onClick={handleSelectPdf}
            sx={{
              width: '100%',
              minHeight: '56px',
              justifyContent: 'flex-start',
              gap: 1.5,
              px: 1.5,
              borderRadius: 1,
              textAlign: 'left',
              '&:hover': {backgroundColor: Pallete.blue.lighter},
              '&.Mui-focusVisible': {
                outline: `2px solid ${Pallete.blue.base}`,
                outlineOffset: -2,
              },
            }}
          >
            <DescriptionIcon aria-hidden="true" sx={{color: Pallete.blue.base, flexShrink: 0}} />
            <Typography component="span">
              {t(translations.export.senior_profile.save_pdf)}
            </Typography>
          </ButtonBase>
        </DialogContent>
      </Dialog>
      <Box height="86px" />
    </ContentPage>
  );
};

export default TaskDetailsDailyCareLogScreen;
